/**
 * Journey conditional logic, validation and cart maths.
 *
 * FRAMEWORK-FREE ON PURPOSE: no Vue, no store, no imports. The CRM preview,
 * the staff read-only view and (later) the guest website all run this same
 * file, so the three can never disagree about which questions are visible.
 *
 * Server counterpart: com.advancedcrm.services.Journey.JourneyConditionService
 * (the server re-evaluates everything on submit and is authoritative).
 */

/**
 * Action steps are performed through the reservation's own endpoints (payment,
 * identity check, signature, guest details). They hold no guest answer — their
 * state comes from `question.task.completed`, computed server-side.
 */
const ACTION_TYPES = ['GUEST_DETAILS', 'IDENTITY_VERIFICATION', 'PAYMENT', 'SIGNATURE'];

export function isActionQuestion(question) {
    return !!question && ACTION_TYPES.indexOf(question.type) >= 0;
}

export function isTaskDone(question) {
    return !!question && !!question.task && question.task.completed === true;
}

function isBlank(value) {
    return value === null || value === undefined || String(value).trim() === '';
}

/** Does this answer hold anything at all? */
export function hasValue(answer) {
    if (answer === null || answer === undefined) return false;
    if (Array.isArray(answer.optionValues) && answer.optionValues.length > 0) return true;
    if (Array.isArray(answer.products) && answer.products.length > 0) return true;
    if (Array.isArray(answer.documentIds) && answer.documentIds.length > 0) return true;
    if (answer.number !== null && answer.number !== undefined) return true;
    if (answer.rating !== null && answer.rating !== undefined) return true;
    if (answer.booleanValue !== null && answer.booleanValue !== undefined) return true;
    return !isBlank(answer.text) || !isBlank(answer.otherText) || !isBlank(answer.date)
        || !isBlank(answer.time) || !isBlank(answer.dateTime);
}

function matches(answer, expected, exact) {
    if (isBlank(expected)) return false;
    const wanted = String(expected).trim().toLowerCase();

    if (Array.isArray(answer.optionValues) && answer.optionValues.length > 0) {
        return answer.optionValues.some(value => {
            const current = String(value === null || value === undefined ? '' : value).toLowerCase();
            return exact ? current === wanted : current.indexOf(wanted) >= 0;
        });
    }
    if (answer.booleanValue !== null && answer.booleanValue !== undefined)
        return String(answer.booleanValue).toLowerCase() === wanted;
    if (answer.number !== null && answer.number !== undefined)
        return String(answer.number).toLowerCase() === wanted;
    if (answer.rating !== null && answer.rating !== undefined)
        return String(answer.rating).toLowerCase() === wanted;

    const text = [answer.text, answer.otherText, answer.date, answer.time, answer.dateTime]
        .find(value => !isBlank(value));
    if (isBlank(text)) return false;
    const current = String(text).trim().toLowerCase();
    return exact ? current === wanted : current.indexOf(wanted) >= 0;
}

/**
 * Ordering for BEFORE/AFTER: numbers numerically, everything else as strings —
 * correct for "HH:mm" times and ISO dates. "9:30" is normalised to "09:30" so
 * single-digit hours sort right. Server mirror: JourneyConditionService.
 * In the builder preview {checkInTime}/{checkOutTime} placeholders are still
 * raw — resolve them to defaults so the preview behaves like a real property.
 */
function normalizeClock(value) {
    return /^\d:\d\d/.test(value) ? '0' + value : value;
}

const PREVIEW_TOKENS = {'{checkInTime}': '15:00', '{checkOutTime}': '11:00'};

function compareOrder(answer, expected) {
    if (isBlank(expected)) return 0;
    let wanted = String(expected).trim();
    Object.keys(PREVIEW_TOKENS).forEach(token => {
        wanted = wanted.split(token).join(PREVIEW_TOKENS[token]);
    });

    let primary = [answer.time, answer.dateTime, answer.date, answer.text].find(value => !isBlank(value));
    if (isBlank(primary) && answer.number !== null && answer.number !== undefined) primary = String(answer.number);
    if (isBlank(primary) && answer.rating !== null && answer.rating !== undefined) primary = String(answer.rating);
    if (isBlank(primary)) return 0;
    const current = String(primary).trim();

    const a = Number(current);
    const b = Number(wanted);
    if (!isNaN(a) && !isNaN(b)) return a === b ? 0 : (a < b ? -1 : 1);

    const left = normalizeClock(current).toLowerCase();
    const right = normalizeClock(wanted).toLowerCase();
    return left === right ? 0 : (left < right ? -1 : 1);
}

/** A node without a condition — or referencing a question that no longer exists — stays visible. */
export function isVisible(condition, answersByKey) {
    if (!condition || !condition.questionKey || !condition.operator) return true;
    const answer = answersByKey ? answersByKey[condition.questionKey] : null;
    const answered = hasValue(answer);

    switch (condition.operator) {
        case 'ANSWERED':
            return answered;
        case 'NOT_ANSWERED':
            return !answered;
        case 'EQUALS':
            return answered && matches(answer, condition.value, true);
        case 'NOT_EQUALS':
            return !answered || !matches(answer, condition.value, true);
        case 'CONTAINS':
            return answered && matches(answer, condition.value, false);
        case 'BEFORE':
            return answered && compareOrder(answer, condition.value) < 0;
        case 'AFTER':
            return answered && compareOrder(answer, condition.value) > 0;
        default:
            return true;
    }
}

export function visibleSteps(journey, answersByKey) {
    if (!journey || !Array.isArray(journey.steps)) return [];
    return journey.steps.filter(step => isVisible(step.condition, answersByKey));
}

export function visibleQuestions(step, answersByKey) {
    if (!step || !Array.isArray(step.questions)) return [];
    return step.questions.filter(question => isVisible(question.condition, answersByKey));
}

/** [{questionKey, error}] for the questions of one step. */
export function validateStep(step, answersByKey) {
    const errors = [];
    visibleQuestions(step, answersByKey).forEach(question => {
        if (question.type === 'INFO') return;
        // action steps: only a "blocking" one that is still open stops the guest
        if (isActionQuestion(question)) {
            if (question.task && question.task.blocking === true && !isTaskDone(question))
                errors.push({questionKey: question.key, error: 'taskrequired'});
            return;
        }
        const answer = answersByKey ? answersByKey[question.key] : null;
        if (question.required && !hasValue(answer)) {
            errors.push({questionKey: question.key, error: 'required'});
            return;
        }
        if (!hasValue(answer)) return;
        if (question.type === 'NUMBER' && answer.number !== null && answer.number !== undefined) {
            if (question.minNumber !== null && question.minNumber !== undefined && answer.number < question.minNumber)
                errors.push({questionKey: question.key, error: 'min'});
            if (question.maxNumber !== null && question.maxNumber !== undefined && answer.number > question.maxNumber)
                errors.push({questionKey: question.key, error: 'max'});
        }
        if (question.type === 'EMAIL' && !isBlank(answer.text) && !/^\S+@\S+\.\S+$/.test(answer.text))
            errors.push({questionKey: question.key, error: 'email'});
        if (question.type === 'CONSENT' && answer.booleanValue !== true)
            errors.push({questionKey: question.key, error: 'required'});
    });
    return errors;
}

/** 0..100 across the visible questions of the whole journey. */
export function progress(journey, answersByKey) {
    let total = 0;
    let done = 0;
    visibleSteps(journey, answersByKey).forEach(step => {
        visibleQuestions(step, answersByKey).forEach(question => {
            if (question.type === 'INFO') return;
            total += 1;
            if (isActionQuestion(question)) {
                if (isTaskDone(question)) done += 1;
                return;
            }
            if (hasValue(answersByKey ? answersByKey[question.key] : null)) done += 1;
        });
    });
    return total === 0 ? 0 : Math.round((done / total) * 100);
}

/** Everything the guest picked that costs money: [{guestFeatureId, name, amount, price, currency}]. */
export function selectedProducts(journey, answersByKey) {
    const picked = [];
    visibleSteps(journey, answersByKey).forEach(step => {
        visibleQuestions(step, answersByKey).forEach(question => {
            const answer = answersByKey ? answersByKey[question.key] : null;
            if (!answer) return;

            // options linked to a guest feature
            if (Array.isArray(answer.optionValues) && Array.isArray(question.options)) {
                answer.optionValues.forEach(value => {
                    const option = question.options.find(o => o.value === value);
                    if (!option || !option.guestFeatureId || option.purchasable === false) return;
                    picked.push({
                        guestFeatureId: option.guestFeatureId,
                        name: option.label,
                        amount: option.amount || 1,
                        price: option.price || 0,
                        currency: option.currency,
                        instantPay: option.instantPay !== false,
                    });
                });
            }
            // product picker selections
            if (Array.isArray(answer.products)) {
                answer.products.forEach(product => {
                    if (!product.guestFeatureId) return;
                    picked.push({
                        guestFeatureId: product.guestFeatureId,
                        name: product.name,
                        amount: product.amount || 1,
                        price: product.price || 0,
                        currency: product.currency,
                        instantPay: product.instantPay !== false,
                    });
                });
            }
        });
    });
    return picked;
}

export function cartTotal(journey, answersByKey) {
    return selectedProducts(journey, answersByKey)
        .reduce((sum, product) => sum + (product.price || 0) * (product.amount || 1), 0);
}

/** answers[] -> {questionKey: answer} */
export function indexAnswers(answers) {
    const map = {};
    (answers || []).forEach(answer => {
        if (answer && answer.questionKey) map[answer.questionKey] = answer;
    });
    return map;
}

/** {questionKey: answer} -> answers[] ready for the save/submit endpoints. */
export function toAnswerRequests(answersByKey) {
    return Object.keys(answersByKey || {}).map(key => {
        const answer = answersByKey[key] || {};
        return {
            questionKey: key,
            text: answer.text,
            number: answer.number,
            booleanValue: answer.booleanValue,
            date: answer.date,
            time: answer.time,
            dateTime: answer.dateTime,
            rating: answer.rating,
            optionValues: answer.optionValues,
            otherText: answer.otherText,
            documentIds: answer.documentIds,
            products: (answer.products || []).map(product => ({
                guestFeatureId: product.guestFeatureId,
                amount: product.amount || 1,
            })),
        };
    });
}

/**
 * Builder journey (raw entity) -> guest view, so the preview can render
 * unsaved edits. The server returns the same shape from /journey/preview/{id}.
 */
export function toGuestView(journey, language, productsById) {
    if (!journey) return null;
    const lang = language || journey.defaultLanguage || 'en';
    const pick = (translations, field, base) => {
        const text = translations ? translations[lang] : null;
        const translated = text ? text[field] : null;
        return translated !== null && translated !== undefined && String(translated).trim() !== '' ? translated : base;
    };
    const products = productsById || {};

    return {
        id: journey.id,
        name: journey.name,
        version: journey.version,
        language: lang,
        availableLanguages: journey.languages || [lang],
        settings: {
            ...(journey.settings || {}),
            welcomeTitle: pick((journey.settings || {}).translations, 'welcomeTitle', (journey.settings || {}).welcomeTitle),
            welcomeText: pick((journey.settings || {}).translations, 'welcomeText', (journey.settings || {}).welcomeText),
            thankYouTitle: pick((journey.settings || {}).translations, 'thankYouTitle', (journey.settings || {}).thankYouTitle),
            thankYouText: pick((journey.settings || {}).translations, 'thankYouText', (journey.settings || {}).thankYouText),
            submitLabel: pick((journey.settings || {}).translations, 'submitLabel', (journey.settings || {}).submitLabel),
            checkoutAvailable: !!(journey.settings || {}).checkoutEnabled,
        },
        steps: (journey.steps || []).map(step => ({
            id: step.id,
            title: pick(step.translations, 'title', step.title),
            subtitle: pick(step.translations, 'subtitle', step.subtitle),
            icon: step.icon,
            condition: step.condition,
            questions: (step.questions || []).map(question => ({
                ...question,
                label: pick(question.translations, 'label', question.label),
                helpText: pick(question.translations, 'helpText', question.helpText),
                placeholder: pick(question.translations, 'placeholder', question.placeholder),
                otherLabel: pick(question.translations, 'otherLabel', question.otherLabel),
                content: pick(question.translations, 'content', question.content),
                options: (question.options || []).map(option => {
                    const offer = option.guestFeatureId ? products[option.guestFeatureId] : null;
                    return {
                        ...option,
                        label: pick(option.translations, 'label', option.label),
                        description: pick(option.translations, 'description', option.description),
                        price: offer ? offer.finalPrice : null,
                        currency: offer ? offer.currency : null,
                        purchasable: option.guestFeatureId ? !!offer : null,
                    };
                }),
            })),
        })),
    };
}
